import {
  Users, Activity, ShieldAlert, Clock, AlertTriangle,
  Server, Key, AlertOctagon
} from "lucide-react";
import { CloudEntityQuickLinks } from "./CloudEntityQuickLinks";
import { EntitySignalBar } from "./EntitySignalBar";

interface ExpandedReviewAgedNhisCardProps {
  data: {
    cloudProvider: string;
    summary: Record<string, any>;
    reviewCandidates: any[];
    severityBreakdown: Record<string, number>;
    recentAlerts: any[];
  }
}

export const ExpandedReviewAgedNhisCard = ({ data }: ExpandedReviewAgedNhisCardProps) => {
  const {
    cloudProvider,
    summary = {},
    reviewCandidates = [],
    severityBreakdown = {},
    recentAlerts = []
  } = data;

  return (
    <div className="space-y-6">
      {/* Header and Hero Stats */}
      <div className="flex items-center gap-3 mb-2">
        <div className="p-2 rounded-xl bg-primary/10">
          <AlertTriangle className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-[15px] text-foreground">Review Aged NHIs</h3>
          <p className="text-[13px] text-muted-foreground">Deep dive into identities flagged for review due to prolonged inactivity, aged credentials, or severe anomalies.</p>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3">
        <div className="p-3.5 rounded-xl bg-card border border-border/40 hover:bg-muted/5 transition-colors">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-sky-500" />
            <span className="text-[12px] font-medium text-muted-foreground">Review Targets</span>
          </div>
          <div className="text-2xl font-bold text-foreground">{summary.reviewCandidates || 0}</div>
        </div>

        <div className="p-3.5 rounded-xl bg-primary/10 border border-primary/20 hover:bg-primary/5 transition-colors">
          <div className="flex items-center gap-2 mb-2">
            <AlertOctagon className="w-4 h-4 text-primary" />
            <span className="text-[12px] font-medium text-primary">High Severity Alerts</span>
          </div>
          <div className="text-2xl font-bold text-primary">{summary.highSeverityAlerts || 0}</div>
        </div>

        <div className="p-3.5 rounded-xl bg-card border border-border/40 hover:bg-muted/5 transition-colors">
          <div className="flex items-center gap-2 mb-2">
            <Key className="w-4 h-4 text-sky-500" />
            <span className="text-[12px] font-medium text-muted-foreground">Aged Credentials</span>
          </div>
          <div className="text-2xl font-bold text-foreground">{summary.agedCredentials || 0}</div>
        </div>

        <div className="p-3.5 rounded-xl bg-card border border-border/40 hover:bg-muted/5 transition-colors">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-sky-500" />
            <span className="text-[12px] font-medium text-muted-foreground">Aged Secrets</span>
          </div>
          <div className="text-2xl font-bold text-foreground">{summary.agedSecrets || 0}</div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Left Column: Ranked Candidates */}
        <div className="col-span-2 space-y-3">
          <h4 className="text-[13px] font-bold text-foreground">Top Candidates for Review</h4>
          <div className="rounded-xl border border-border/50 bg-card overflow-hidden max-h-[520px] overflow-y-auto custom-scrollbar">
             {reviewCandidates.length > 0 ? (
               <div className="divide-y divide-border/20">
                 {reviewCandidates.map((candidate, idx) => (
                   <div key={idx} className="p-3 hover:bg-muted/10 transition-colors">
                     <div className="flex gap-3">
                       <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                         {candidate.type?.includes("SERVICE_ACCOUNT") ? <Server className="w-4 h-4 text-primary" /> : <Users className="w-4 h-4 text-primary" />}
                       </div>
                       <div className="flex-1 min-w-0">
                         <div className="flex items-center justify-between gap-4 mb-2">
                           <div className="min-w-0">
                             <h4 className="font-semibold text-[13px] text-foreground truncate" title={candidate.name}>{candidate.name}</h4>
                             <p className="text-[11px] text-muted-foreground mt-0.5 max-w-[90%] truncate" title={candidate.reason}>{candidate.reason || 'Multiple risk vectors identified'}</p>
                             <CloudEntityQuickLinks
                               cloudProvider={cloudProvider}
                               entityKind="nhi"
                               entity={candidate}
                               className="mt-2"
                             />
                           </div>
                           <div className="flex items-center gap-4 shrink-0">
                              <div className="flex gap-1">
                                {(candidate.severityBreakdown?.critical > 0) && (
                                  <span className="px-1.5 py-0.5 bg-primary/20 text-primary text-[10px] font-bold rounded">
                                    {candidate.severityBreakdown.critical} CRIT
                                  </span>
                                )}
                                {(candidate.severityBreakdown?.high > 0) && (
                                  <span className="px-1.5 py-0.5 bg-primary/10 text-primary/80 text-[10px] font-bold rounded border border-primary/20">
                                    {candidate.severityBreakdown.high} HIGH
                                  </span>
                                )}
                              </div>
                           </div>
                         </div>
                         
                         {/* Details Grid */}
                         <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-border/10">
                            <EntitySignalBar
                              icon={ShieldAlert}
                              label="Alerts"
                              helperText={String(candidate.alertCount || 0)}
                              percentage={candidate.alertPct}
                              accentClassName="text-primary/80"
                              trackClassName="bg-primary/10"
                              fillClassName="bg-primary"
                            />
                            <EntitySignalBar
                              icon={Activity}
                              label="Activity"
                              percentage={candidate.activityPct}
                              accentClassName="text-sky-500/80"
                              trackClassName="bg-sky-500/10"
                              fillClassName="bg-sky-500"
                            />
                            <div className="rounded-lg border border-border/15 bg-muted/20 px-3 py-2">
                              <span className="text-[10px] text-muted-foreground">Active Keys</span>
                              <span className="text-[12px] font-medium mt-1 flex items-center gap-1.5">
                                <Key className="w-3 h-3 text-foreground" />
                                {candidate.keyCount} {candidate.agedKeys > 0 && <span className="text-primary text-[10px]">({candidate.agedKeys} aged)</span>}
                              </span>
                            </div>
                            <div className="rounded-lg border border-border/15 bg-muted/20 px-3 py-2">
                              <span className="text-[10px] text-muted-foreground">Last Activity</span>
                              <span className="text-[12px] font-medium mt-1 block truncate" title={candidate.lastActivity || 'Unknown'}>
                                {candidate.lastActivity ? new Date(candidate.lastActivity).toLocaleDateString() : 'None'}
                              </span>
                            </div>
                         </div>
                       </div>
                     </div>
                   </div>
                 ))}
               </div>
             ) : (
               <div className="p-8 text-center text-muted-foreground text-[13px]">No review candidates tracked.</div>
             )}
          </div>
        </div>

        {/* Right Column: Recent Anomaly Alerts */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-[13px] font-bold text-foreground">Candidate Alert Logs</h4>
            <div className="flex items-center gap-1 text-[10px] font-bold bg-muted/10 px-2 py-0.5 rounded border border-border/10 text-muted-foreground">
               CRIT: <span className="text-primary">{severityBreakdown.critical || 0}</span>
               <span className="mx-1 text-border">|</span>
               HIGH: <span className="text-primary/70">{severityBreakdown.high || 0}</span>
            </div>
          </div>
          
          <div className="rounded-xl border border-border/50 bg-card overflow-hidden">
            {recentAlerts.length > 0 ? (
              <div className="divide-y divide-border/10 max-h-[500px] overflow-y-auto custom-scrollbar">
                {recentAlerts.map((alert, idx) => {
                  const isPrimary = alert.severity?.toLowerCase() === 'critical' || alert.severity?.toLowerCase() === 'high';
                  return (
                    <div key={idx} className="p-3 text-[12px]">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${isPrimary ? 'bg-primary/10 text-primary border border-primary/20' : 'bg-sky-500/10 text-sky-500 border border-sky-500/20'}`}>
                          {alert.severity || "INFO"}
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                          {alert.createdAt ? new Date(alert.createdAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'}) : ''}
                        </span>
                      </div>
                      <p className="font-semibold text-foreground leading-tight mb-1">{alert.title}</p>
                      <p className="text-[11px] text-muted-foreground truncate" title={alert.entity}>{alert.entity}</p>
                    </div>
                  );
                })}
              </div>
            ) : (
               <div className="p-6 text-center flex flex-col items-center gap-2">
                 <ShieldAlert className="w-6 h-6 text-muted-foreground/30" />
                 <span className="text-muted-foreground text-[12px]">No recent alerts</span>
               </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
